import React, { FC, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import { uploadHotelBiddingCriteria } from '~/services/PublicOfferFeedService';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
}) as FC<any>;

interface Props {
  customerId: string;
  setOpen: (open: boolean) => void;
}

function HotelBiddingCriteriaManagementForm({ customerId, setOpen }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const handleClose = () => {
    setOpen(false);
  };

  const { handleSubmit } = useForm();

  const handleFileUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar('No file selected');
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      await uploadHotelBiddingCriteria(customerId, selectedFile);
      enqueueSnackbar(`File uploaded successfully.`, { variant: 'success' });
      setOpen(false);
    } catch (error) {
      enqueueSnackbar(`Error uploading file: ${error}`, { variant: 'error' });
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open
      onClose={handleClose}
      onSubmit={handleSubmit(handleFileUpload)}
      component="form"
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="actual-title">
        Uploading Hotel Bidding Criteria
      </DialogTitle>
      <DialogContent dividers>
        <Stack>
          <Grid container spacing={1}>
            <Grid>
              <Alert severity="info" sx={{ my: 2 }}>
                <Typography variant="body2">
                  <strong>Instructions:</strong> Please make sure your CSV file includes all{' '}
                  <strong>four required columns</strong> for hotel bidding criteria:
                  <ul>
                    <li>
                      <strong>HotelId</strong> – Unique Hotel ID (UUID)
                    </li>
                    <li>
                      <strong>CampaignId</strong> – Google Ads Campaign identifier
                    </li>
                    <li>
                      <strong>Action</strong> – Either <code>Upsert</code> or <code>Delete</code>
                    </li>
                    <li>
                      <strong>Bid</strong> – Bid percentage (leave empty when using <code>Delete</code> action)
                    </li>
                  </ul>
                </Typography>
              </Alert>
              <Stack spacing={2}>
                <Button
                  component="label"
                  role={undefined}
                  variant="contained"
                  tabIndex={-1}
                  disabled={!!selectedFile}
                  startIcon={selectedFile ? <CheckBoxIcon /> : <CloudUploadIcon />}
                >
                  {selectedFile ? 'File attached' : 'Upload files'}
                  <VisuallyHiddenInput
                    type="file"
                    accept=".csv, text/csv"
                    onChange={(event) => setSelectedFile(event.target.files[0])}
                    multiple
                  />
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" type="submit">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default HotelBiddingCriteriaManagementForm;
